import { Grid2 } from '@mui/material';
import CustomAutocomplete from '../HomeScreen/CustomAutocomplete';
import React, { useEffect, useMemo, useState } from 'react';
import useUrlParams from '../../hooks/useUrlParams';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../store/actions';

const CoreFilters = ({ filtersUpdated, targetAssetIdsUpdated }) => {
  const dispatch = useDispatch();

  const [filterDisabled, setFilterDisabled] = useState(false);
  const { portfolios } = useSelector((state) => state.portfolios);
  const { data: allManagers } = useSelector((state) => state.assets.assetManagers);
  const { data: assets, loading: assetsLoading } = useSelector((state) => state.assets.assets);
  const {
    portfolioIds, setPortfolios, assetIds, setAssetIds, assetType, setAssetType,
    assetsStatus, setAssetsStatus, managers, setManagers
  } = useUrlParams();
  const [targetAssetIds, setTargetAssetIds] = useState([]);
  const [initialChecks, setInitialChecks] = useState(false);

  const filteredAssets = useMemo(() => assets
    .filter((item) => targetAssetIds.length === 0 || targetAssetIds.includes(item.id))
    .reduce((accumulator, currentObject) => {
      if (!accumulator.find((obj) => obj.id === currentObject.id)) {
        accumulator.push(currentObject);
      }
      return accumulator;
    }, [])
    .map((item) => {
      return { id: item.id, name: item.name };
    }), [assets, targetAssetIds]);

  const assetTypes = useMemo(() => assets
    .filter((item) => targetAssetIds.length === 0 || targetAssetIds.includes(item.id))
    .reduce((accumulator, currentObject) => {
      if (!accumulator.find((obj) => obj.type === currentObject.type)) {
        accumulator.push(currentObject);
      }
      return accumulator;
    }, [])
    .map((item) => {
      return { id: item.type, name: item.type };
    }), [assets, targetAssetIds]);

  const assetsStatusList = useMemo(() => assets
    .filter((item) => targetAssetIds.length === 0 || targetAssetIds.includes(item.id))
    .reduce((accumulator, currentObject) => {
      if (!accumulator.find((obj) => obj.status === currentObject.status)) {
        accumulator.push(currentObject);
      }
      return accumulator;
    }, [])
    .map((item) => {
      return { id: item.status, name: item.status };
    }), [assets, targetAssetIds]);

  const managersOptions = useMemo(
    () => allManagers
      .filter((item) => targetAssetIds.length === 0 ||
        item.assetidsList.some(id => targetAssetIds.includes(id))
      )
      .map(it => ({ ...it, id: it.manager, name: it.manager })),
    [allManagers, targetAssetIds]
  );

  useEffect(() => {
    dispatch(actions.getAssetManagers());
  }, []);

  useEffect(() => {
    if (!portfolioIds) {
      return;
    }

    dispatch(actions.getAssets(portfolioIds));
  }, [portfolioIds]);

  useEffect(() => {
    if (JSON.stringify(targetAssetIds) === JSON.stringify(assets.map((item) => item.id))) {
      return;
    }

    let localAssetStatus = assetsStatus;
    if (!initialChecks) {
      const filterIsEmpty =
        portfolioIds.length === 0 &&
        assetIds.length === 0 &&
        assetType.length === 0 &&
        assetsStatus.length === 0 &&
        managers.length === 0;
      const underManagementCount = assets.filter(asset => asset.status === 'Under Management').length;

      if (filterIsEmpty && underManagementCount > 1) {
        localAssetStatus = ['Under Management'];
        setAssetsStatus(['Under Management']);
      }

      setInitialChecks(true);
    }

    const newTargetAssetIds = calculateNewTargetAssetIds(assetIds, assetType, localAssetStatus, managers);
    setTargetAssetIds(newTargetAssetIds);
  }, [assets]);

  useEffect(() => {
    if (JSON.stringify(targetAssetIds) === JSON.stringify(assetIds)) {
      return;
    }

    const newTargetAssetIds = calculateNewTargetAssetIds(assetIds, assetType, assetsStatus, managers);

    if (JSON.stringify(targetAssetIds) === JSON.stringify(newTargetAssetIds)) {
      return;
    }

    setTargetAssetIds(newTargetAssetIds);
  }, [assetIds, assetType, assetsStatus, managers]);

  useEffect(
    () => filtersUpdated({ portfolioIds, assetIds, assetTypes: assetType, assetsStatus, managers }),
    [portfolioIds, assetIds, assetType, assetsStatus, managers]
  );

  useEffect(() => targetAssetIdsUpdated(targetAssetIds), [targetAssetIds]);

  const updateAssetsStatuses = (statuses) => {
    const newTargetAssetIds = calculateNewTargetAssetIds(assetIds, assetType, statuses, managers);
    setTargetAssetIds(newTargetAssetIds);
    setAssetsStatus(statuses);
  };

  const updateAssetType = (types) => {
    const newTargetAssetIds = calculateNewTargetAssetIds(assetIds, types, assetsStatus, managers);
    setTargetAssetIds(newTargetAssetIds);
    setAssetType(types);
  };

  const updateManagers = (managers) => {
    const newTargetAssetIds = calculateNewTargetAssetIds(assetIds, assetType, assetsStatus, managers);
    setTargetAssetIds(newTargetAssetIds);
    setManagers(managers);
  };

  const updateAssetIds = (ids) => {
    const newTargetAssetIds = calculateNewTargetAssetIds(ids, assetType, assetsStatus, managers);
    setTargetAssetIds(newTargetAssetIds);
    setAssetIds(ids);
  };

  const calculateNewTargetAssetIds = (assetIds, assetType, assetsStatus, managers) => {
    return assets
      .filter(
        (item) =>
          (assetIds.length === 0 || assetIds.includes(item.id)) &&
          (assetType.length === 0 || assetType.includes(item.type)) &&
          (assetsStatus.length === 0 || assetsStatus.includes(item.status)) &&
          (managers.length === 0 || managers.includes(item.manager))
      )
      .map((item) => item.id);
  };

  const width = '16%';

  return (
    <Grid2 container sx={{ padding: '20px 0' }} spacing={5}>
      <Grid2 item style={{ width }}>
        <CustomAutocomplete
          items={portfolios}
          updateUrlParams={(values) => {
            setFilterDisabled(true);
            setTimeout(() => setFilterDisabled(false), 1000);
            setPortfolios(values);
          }}
          urlParams={portfolioIds}
          label="Portfolios"
          placeholder="Portfolios"
          disabled={!!assetIds.length || filterDisabled || assetsLoading}
        />
      </Grid2>
      <Grid2 item style={{ width }}>
        <CustomAutocomplete
          items={filteredAssets}
          updateUrlParams={(values) => {
            setFilterDisabled(true);
            setTimeout(() => setFilterDisabled(false), 1000);
            updateAssetIds(values);
          }}
          urlParams={assetIds}
          label="Assets"
          placeholder="Assets"
          disabled={filterDisabled}
        />
      </Grid2>
      <Grid2 item style={{ width }}>
        <CustomAutocomplete
          updateUrlParams={(values) => {
            setFilterDisabled(true);
            setTimeout(() => setFilterDisabled(false), 1000);
            updateAssetType(values);
          }}
          urlParams={assetType}
          items={assetTypes}
          label="Asset Types"
          placeholder="Asset types"
          disabled={filterDisabled}
        />
      </Grid2>
      <Grid2 item style={{ width }}>
        <CustomAutocomplete
          updateUrlParams={(values) => {
            setFilterDisabled(true);
            setTimeout(() => setFilterDisabled(false), 1000);
            updateAssetsStatuses(values);
          }}
          urlParams={assetsStatus}
          items={assetsStatusList}
          label="Asset Status"
          placeholder="Asset Status"
          disabled={filterDisabled}
        />
      </Grid2>
      <Grid2 item style={{ width }}>
        <CustomAutocomplete
          updateUrlParams={(values) => {
            setFilterDisabled(true);
            setTimeout(() => setFilterDisabled(false), 1000);
            updateManagers(values);
          }}
          urlParams={managers}
          items={managersOptions}
          label="Asset Managers"
          placeholder="Asset Managers"
          disabled={filterDisabled}
        />
      </Grid2>
    </Grid2>
  );
};

export default CoreFilters;
